import { ProviderClaimPayment, ProviderClaimPaymentItem } from '../../api';
import { IDenialRemarkRow } from './denial-remark-rows';

/**
 * The payments behind one row of the denials table — the design's drill-down.
 *
 * ## What the server narrows, and what this keeps exact
 *
 * The caller fetches payments by REASON CODE alone (`filterAdjustmentReason`), not by payment status.
 * An earlier version fetched `filterStatus=EQ:DENIED` and found nothing for a row with 198
 * adjustments: a denial ADJUSTMENT is not a DENIED PAYMENT. Measured on production 2026-10-01, every
 * `OA:23` payment in a week was PROCESSED_AS_SECONDARY or a REVERSAL, and every `CO:B13` payment was
 * PROCESSED_AS_PRIMARY — none was DENIED. A line can be denied while its payment as a whole is paid.
 *
 * This function then keeps the result exact on the two things the server was not asked about: the
 * adjustment GROUP (PR:23 is not OA:23 — adding the group to the query took 8.0 s against 1.4 s and
 * returned the same set, so it is cheaper to check here) and the REMARK SET. `filterRemark` does
 * scope (re-measured 2026-10-05: 19 of 19 rows carried the remark — an earlier note here said it
 * returned nothing, which could not be reproduced), but it matches a remark ANYWHERE on the payment,
 * while a row is one line's exact set, and a row with no remark has nothing to filter by — so the set
 * is checked per line here either way.
 *
 * ## What "behind this row" means
 *
 * A row of the denials table is one `(group, reasonCode, remark set)` triple, because that is how
 * `adjustment-remark` groups. The remark set belongs to the SERVICE LINE, not to the adjustment, so
 * a payment belongs to a row when **one of its line items** carries an adjustment with that group
 * and reason code AND that same item's remark codes are exactly the row's set.
 *
 * Matching the payment's own `remarkCodes` instead would be wrong: a payment carries the union of
 * its lines' remarks, so a payment with two lines — one `CO:197 · N54` and one `CO:45 · M80` — would
 * match both rows on either remark. The item is the only grain where the pairing holds.
 *
 * ## The list repeats payments
 *
 * `/provider/payment` filtered by adjustment reason returns the SAME payment two or three times.
 * Measured on production 2026-10-01: a week of reason `23` came back as 256 rows holding 185 distinct
 * payments, and the first page of 100 held 74. Every repeat was identical to its first copy once
 * items and adjustments were sorted (71/71 for `23`, 31/31 for `B13`) — the server returns one copy
 * per matching line, in no stable item order. Paging does not lose payments over it: for `B13` the
 * paged walk and a union of single-day queries found the same 80. So each payment is kept once, by
 * id; without that, a payment with the code on three lines counted three times.
 */
export interface IDenialPaymentRow {
    id: string;
    /** When the payer paid, and when we received it — the design shows both on one cell. */
    paymentDate?: string;
    receivedDate?: string;
    payerControlNumber?: string;
    status?: string;
    charge: number;
    allowed: number;
    payerPaid: number;
    patientResponsibility: number;
    /** How many service lines the payment covers. */
    lines: number;
    /** The adjustments on the matching lines only, not every adjustment on the payment. */
    adjustments: Array<{ code: string; amount: number }>;
    /** The remark set of the matching lines — the row's own set, by construction. */
    remarkCodes: string[];
    /** The CPT/HCPCS codes of the matching lines only — the lines that were denied. */
    procedureCodes: string[];
    /** Every line's procedure code, in line order, repeats kept — the design's Procedures column. */
    allProcedureCodes: string[];
    /** The sum of this row's adjustment on the matching lines — the design's `CO-50 Adj.` column. */
    matchedAmount: number;
    memo?: string;
    eobId?: string;
    /** Every line on the remittance, for the expanded ERA card. */
    items: IDenialPaymentLine[];
}

/** One service line of the remittance, as the ERA card shows it. */
export interface IDenialPaymentLine {
    controlNumber?: string;
    procedureCode?: string;
    modifierCodes: string[];
    fromDos?: string;
    throughDos?: string;
    placeOfService?: string;
    units?: number;
    charge: number;
    allowed: number;
    paid: number;
    /** `CO-45 · $120.00`; `matched` marks the row's own code, which the card shows in red. */
    adjustments: Array<{ code: string; amount: number; matched: boolean }>;
    remarkCodes: string[];
    /** This line carries the row's code with the row's remark set — the line the card highlights. */
    matched: boolean;
}

const GROUP_SHORT: Record<string, string> = {
    CONTRACTUAL_OBLIGATIONS: 'CO',
    OTHER_ADJUSTMENTS: 'OA',
    PAYER_INITIATED_REDUCTIONS: 'PI',
    PATIENT_RESPONSIBILITY: 'PR',
    CORRECTIONS_AND_REVERSALS: 'CR',
};

const money = (value: unknown): number => {
    const raw = value && typeof value === 'object' ? (value as { amount?: string }).amount : value;
    const parsed = Number(raw);

    return Number.isFinite(parsed) ? parsed : 0;
};

const remarkSet = (codes: string[] | undefined): string[] =>
    [...new Set((codes || []).map(code => code.trim().toUpperCase()).filter(Boolean))].sort();

const sameSet = (a: string[], b: string[]): boolean =>
    a.length === b.length && a.every((value, index) => value === b[index]);

/** The line items of one payment that belong to the given row. */
const matchingItems = (
    payment: ProviderClaimPayment,
    row: IDenialRemarkRow,
): ProviderClaimPaymentItem[] =>
    (payment.items || []).filter(item => {
        const carries = (item.adjustments || []).some(
            adjustment =>
                adjustment.group === row.code.group && adjustment.reasonCode === row.code.reasonCode,
        );

        return carries && sameSet(remarkSet(item.remarkCodes), row.remarkCodes);
    });

export const selectPaymentsForRow = (
    payments: ProviderClaimPayment[] | undefined,
    row: IDenialRemarkRow,
): IDenialPaymentRow[] => {
    const rows: IDenialPaymentRow[] = [];
    const seen = new Set<string>();

    (payments || []).forEach(payment => {
        // The list repeats a payment once per matching line; the copies are identical, keep the first.
        if (payment.id && seen.has(payment.id)) {
            return;
        }
        if (payment.id) {
            seen.add(payment.id);
        }
        const items = matchingItems(payment, row);
        if (!items.length) {
            return;
        }

        const matched = new Set(items);
        const isRowCode = (adjustment: { group?: string; reasonCode?: string }): boolean =>
            adjustment.group === row.code.group && adjustment.reasonCode === row.code.reasonCode;
        const lineItems = (payment.items || []).map(
            (item): IDenialPaymentLine => ({
                controlNumber: item.controlNumber,
                procedureCode: item.procedureCode,
                modifierCodes: item.modifierCodes || [],
                fromDos: item.fromDos,
                throughDos: item.throughDos,
                placeOfService: item.placeOfServiceCode,
                units: item.chargeCount,
                charge: money(item.chargeAmount),
                allowed: money(item.allowedAmount),
                paid: money(item.paidAmount),
                adjustments: (item.adjustments || []).map(adjustment => ({
                    code: `${GROUP_SHORT[adjustment.group || ''] || adjustment.group || '?'}-${adjustment.reasonCode || '?'}`,
                    amount: money(adjustment.amount),
                    matched: matched.has(item) && isRowCode(adjustment),
                })),
                remarkCodes: item.remarkCodes || [],
                matched: matched.has(item),
            }),
        );

        rows.push({
            id: payment.id || '',
            paymentDate: payment.paymentDate,
            receivedDate: payment.receivedDate,
            payerControlNumber: payment.payerControlNumber,
            status: payment.status,
            charge: money(payment.chargeAmount),
            allowed: money(payment.allowedAmount),
            payerPaid: money(payment.payerAmount),
            patientResponsibility: money(payment.patientAmount),
            // Every line on the payment, which is what the design's "2 lines" counts — not only the
            // lines that matched. A biller opening this is looking at the whole remittance.
            lines: (payment.items || []).length,
            adjustments: items.flatMap(item =>
                (item.adjustments || [])
                    .filter(
                        adjustment =>
                            adjustment.group === row.code.group &&
                            adjustment.reasonCode === row.code.reasonCode,
                    )
                    .map(adjustment => ({ code: row.codes, amount: money(adjustment.amount) })),
            ),
            // No claim id here: the payment's `professionalId` is the professional-claim SUB-RECORD's
            // id, and the claim endpoints answer 500 for it. The drawer resolves the claim from the
            // payment id instead (`/provider/claim?filterPaymentId=`).
            remarkCodes: row.remarkCodes,
            procedureCodes: [
                ...new Set(items.map(item => (item.procedureCode || '').trim()).filter(Boolean)),
            ],
            allProcedureCodes: (payment.items || [])
                .map(item => (item.procedureCode || '').trim())
                .filter(Boolean),
            matchedAmount: lineItems
                .flatMap(line => line.adjustments)
                .filter(adjustment => adjustment.matched)
                .reduce((total, adjustment) => total + adjustment.amount, 0),
            memo: payment.memo,
            eobId: payment.eobId,
            items: lineItems,
        });
    });

    // Newest first: a biller works the most recent remittance, and the design lists by payment date.
    rows.sort((a, b) => (b.paymentDate || '').localeCompare(a.paymentDate || ''));

    return rows;
};

/**
 * The count the drill-down header shows.
 *
 * Distinct payments, not adjustments and not claims — and it does NOT reconcile with the row's
 * `adjustments` figure. The reasons, the last unconfirmed: one payment can carry the code on several
 * lines; a reversal and its reprocessing are two payments for one event; and the adjustment report and
 * the payment list most likely date things by different fields (payments here are filtered by
 * `paymentDate`). The drill-down says so on screen rather than letting the mismatch read as a bug.
 */
export const denialPaymentTotals = (
    rows: IDenialPaymentRow[],
): { payments: number; charge: number } => ({
    payments: rows.length,
    // Safe to sum, unlike the rejection charges: `selectPaymentsForRow` keeps each payment once.
    charge: rows.reduce((total, row) => total + row.charge, 0),
});
